import { Box, Button } from '@chakra-ui/react';
import { useState } from 'react';
import ErrorState from '../../../components/states/ErrorState';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import { CourtConfigurationList } from '../components/CourtConfigurationList';
import { CreateCourtConfigurationForm } from '../components/CreateCourtConfigurationForm';
import { useIsOrgManager } from '../../auth/roles';
import { useCreateCourtConfiguration } from '../hooks/useCreateCourtConfiguration';
import { useGetCourtConfigurations } from '../hooks/useGetCourtConfigurations';

type Props = {
    courtId: number;
}
export const CourtConfigurationPage = ({ courtId }: Props) => {
    // `POST courts/:id/configurations` is `hasOrganizationAdminAccess` on the
    // API, while `GET :id/configurations` is membership-level — so a coach may
    // read this list but every write here 403s. Without this the button was
    // rendered to them: reachable by hand-typing `?tab=configuration`, whose
    // SegmentedControl entry is commented out in `CourtDetailsPage`.
    const isOrgManager = useIsOrgManager();
    const [isCreating, setIsCreating] = useState(false);

    const { data: configurations, isLoading, isError } = useGetCourtConfigurations(courtId);
    const createMutation = useCreateCourtConfiguration(courtId);

    return (
        <PageCard
            showBackButton
            title={translate('court_configuration.title')}
            rightHeader={
                isOrgManager && !isCreating && (
                    <Button
                        colorScheme="brand"
                        onClick={() => setIsCreating(true)}
                    >
                        {translate('court_configuration.create_new')}
                    </Button>
                )
            }
        >
            <Box p={4}>
                {/* `isOrgManager` again rather than relying on the button
                    above being the only way in: the create form is the thing
                    that fires the admin-only mutation. */}
                {isCreating && isOrgManager ? (
                    <Box mb={4}>
                        <CreateCourtConfigurationForm
                            onSubmit={async (data) => {
                                try {
                                    await createMutation.mutateAsync(data);
                                    setIsCreating(false);
                                } catch {
                                    // error toast handled by mutation onError
                                }
                            }}
                            onCancel={() => setIsCreating(false)}
                            isLoading={createMutation.isLoading}
                        />
                    </Box>
                ) : (
                    <Box>
                        {isLoading ? (
                            <Box>{translate('court_configuration.loading')}</Box>
                        ) : isError ? (
                            <ErrorState message={translate('error')} />
                        ) : configurations && configurations.length > 0 ? (
                            <CourtConfigurationList configurations={configurations} />
                        ) : (
                            <Box>{translate('court_configuration.no_configurations')}</Box>
                        )}
                    </Box>
                )}
            </Box>
        </PageCard>
    );
};